/**
 * Board adjacency helpers.
 *
 * The `board.json` file stores each edge once (only one direction); Scotland
 * Yard edges are undirected, so we expand each edge into both directions.
 *
 * Pure: builds an immutable adjacency map from a `Board` value. The engine
 * functions take a `Board` parameter so callers can inject test fixtures.
 */

import type { Board, Transport } from '../types'
import boardData from '../data/board.json'

export interface AdjacentEdge {
  /** Neighbouring station id. */
  to: number
  /** Transport type required for this edge. */
  transport: Transport
}

export type Adjacency = ReadonlyMap<number, readonly AdjacentEdge[]>

/** Default board (the canonical 199-station London graph). */
export const DEFAULT_BOARD: Board = boardData as Board

/** Build an undirected adjacency map keyed by station id. */
export function buildAdjacency(board: Board): Adjacency {
  const map = new Map<number, AdjacentEdge[]>()
  for (const station of board.stations) {
    map.set(station.id, [])
  }
  for (const edge of board.edges) {
    const fromList = map.get(edge.from)
    const toList = map.get(edge.to)
    if (!fromList || !toList) continue
    fromList.push({ to: edge.to, transport: edge.transport })
    toList.push({ to: edge.from, transport: edge.transport })
  }
  return map
}

let _defaultAdjacency: Adjacency | null = null

/** Memoised adjacency for the default board. */
export function defaultAdjacency(): Adjacency {
  if (_defaultAdjacency === null) {
    _defaultAdjacency = buildAdjacency(DEFAULT_BOARD)
  }
  return _defaultAdjacency
}
